import type { FleetUnitVehicle } from '@chukua/common';
import type { TabsProps } from 'antd';
import type { ReactNode } from 'react';
import type { TruckClass } from '../../../../reference-data/reference-data.api';
import { TruckPartLifeTab } from '../../../../maintenance/features/parts/public/TruckPartLifeTab';
import { TruckPartsTab } from '../../../../maintenance/features/parts/public/TruckPartsTab';
import { TruckServicePlanTab } from '../../../../maintenance/features/truck-service/public/TruckServicePlanTab';
import { TruckServiceRecordsTab } from '../../../../maintenance/features/truck-service/public/TruckServiceRecordsTab';
import { TruckScheduleTab } from '../../../../maintenance/features/workshop-bookings/public/TruckScheduleTab';
import { CouplingHistoryTable } from '../../../components/CouplingHistoryTable';
import { TrailerDetailsForm } from '../../trailers/components/TrailerDetailsForm';
import { TrailerDocuments } from '../../trailers/components/TrailerDocuments';
import { TrailerHistoryTab } from '../../trailers/components/TrailerHistoryTab';
import { TrailerOwnershipTab } from '../../trailers/components/TrailerOwnershipTab';
import { TruckDetailsTab } from '../../trucks/components/TruckDetailsTab';
import { TruckDocuments } from '../../trucks/components/TruckDocuments';
import { TruckHistoryTab } from '../../trucks/components/TruckHistoryTab';
import { TruckOwnershipTab } from '../../trucks/components/TruckOwnershipTab';
import type { useTruckDetail } from '../../trucks/hooks/use-truck-detail';
import type { TruckDetail } from '../../trucks/trucks.api';
import { UNIT_TAB_LABELS, UNIT_TABS, isMaintenanceTab, type UnitTab } from '../units.paths';
import { UnitTrailerSection, type UnitTrailerParts } from './UnitTrailerSection';
import { UnitTyresTab } from './UnitTyresTab';
import { VehicleSection } from './VehicleSection';

export interface BuildUnitTabsArgs {
  truck: TruckDetail;
  trailer: FleetUnitVehicle | null;
  truckClasses: TruckClass[];
  canManageTruck: boolean;
  canManageDocuments: boolean;
  canViewMaintenance: boolean;
  detail: ReturnType<typeof useTruckDetail>;
  /** Bumped after a couple, swap or drop, so the coupling history reloads. */
  couplingVersion: number;
  /** After the trailer's record is saved, so the rig drawn above the tabs takes its new type or colour. */
  onTrailerSaved: () => void;
}

/** The truck's part of a tab, then the trailer's when the unit has one. */
function bothVehicles(args: BuildUnitTabsArgs, truckPart: ReactNode, trailerPart: (parts: UnitTrailerParts) => ReactNode): ReactNode {
  return (
    <div className="flex flex-col gap-8">
      <VehicleSection vehicleKind="truck" registration={args.truck.registration}>
        {truckPart}
      </VehicleSection>
      {args.trailer && (
        <UnitTrailerSection key={args.trailer.id} trailer={args.trailer}>
          {trailerPart}
        </UnitTrailerSection>
      )}
    </div>
  );
}

function detailsTab(args: BuildUnitTabsArgs): ReactNode {
  const { truck, truckClasses, canManageTruck, detail } = args;
  return bothVehicles(
    args,
    <TruckDetailsTab
      truck={truck}
      truckClasses={truckClasses}
      canManage={canManageTruck}
      error={detail.updateError}
      saving={detail.saving}
      device={detail.device}
      onSave={detail.update}
      onRecordReading={detail.recordReading}
    />,
    ({ trailer, detail: trailerDetail, canManage }) => (
      <TrailerDetailsForm
        trailer={trailer}
        canManage={canManage}
        error={trailerDetail.updateError}
        saving={trailerDetail.saving}
        onSave={async (input) => {
          const saved = await trailerDetail.update(input);
          if (saved) args.onTrailerSaved();
          return saved;
        }}
      />
    ),
  );
}

function documentsTab(args: BuildUnitTabsArgs): ReactNode {
  const { truck, canManageDocuments, detail } = args;
  return bothVehicles(
    args,
    <TruckDocuments
      documents={truck.documents}
      canManage={canManageDocuments}
      submitting={detail.documentSubmitting}
      onAdd={detail.addDocument}
      onOpen={detail.openDocument}
    />,
    ({ trailer, detail: trailerDetail, canManageDocuments: canManage }) => (
      <TrailerDocuments
        documents={trailer.documents}
        canManage={canManage}
        submitting={trailerDetail.documentSubmitting}
        onAdd={trailerDetail.addDocument}
        onOpen={trailerDetail.openDocument}
      />
    ),
  );
}

/** Each tab's content, by key; the order the tabs show in is `UNIT_TABS`. */
function tabContent(args: BuildUnitTabsArgs): Record<UnitTab, () => ReactNode> {
  const { truck, trailer } = args;
  return {
    details: () => detailsTab(args),
    tyres: () => <UnitTyresTab truck={truck} trailer={trailer} />,
    documents: () => documentsTab(args),
    'service-plan': () => <TruckServicePlanTab truckId={truck.id} classCode={truck.classCode} />,
    'service-records': () => <TruckServiceRecordsTab truckId={truck.id} />,
    schedule: () => <TruckScheduleTab truckId={truck.id} />,
    parts: () => <TruckPartsTab truckId={truck.id} />,
    'part-life': () => <TruckPartLifeTab truckId={truck.id} />,
    ownership: () =>
      bothVehicles(
        args,
        <TruckOwnershipTab truckId={truck.id} truckRegistration={truck.registration} canManage={args.canManageTruck} />,
        ({ trailer: record, canManage }) => <TrailerOwnershipTab trailerId={record.id} trailerRegistration={record.registration} canManage={canManage} />,
      ),
    couplings: () => <CouplingHistoryTable key={args.couplingVersion} vehicleKind="truck" vehicleId={truck.id} />,
    history: () =>
      bothVehicles(args, <TruckHistoryTab truckId={truck.id} statusHistory={truck.statusHistory} />, ({ trailer: record }) => (
        <TrailerHistoryTab trailerId={record.id} />
      )),
  };
}

/**
 * A unit's tabs (gitaumoses4/chukua#390): each shows the truck and, under it, the trailer it pulls, so the trailer's tyres and
 * papers are a tab away from the truck's. Service, schedule, parts and part life are the truck's alone; trailers have no service plan or
 * work orders yet. The maintenance tabs need `maintenance.view`.
 */
export function buildUnitTabs(args: BuildUnitTabsArgs): TabsProps['items'] {
  const content = tabContent(args);
  return UNIT_TABS.filter((tab) => args.canViewMaintenance || !isMaintenanceTab(tab)).map((tab) => ({
    key: tab,
    label: UNIT_TAB_LABELS[tab],
    children: content[tab](),
  }));
}
